import { CreateQuizResponseInput, QuizSource } from 'src/graphqlTypes/catalogServiceTypes';
import { Filters as AlgoliaFilters, AlgoliaFacets } from 'src/clients/Algolia/constants';
import { search, searchForFacetValues } from 'src/clients/Algolia';
import { OfferingHit } from '@src/queries/user';
import {
	eduLevelMap,
	educationFacetMap,
	educationalGoals,
	goalsFacetMap,
	gpaMap,
	screenSelectionTypeMap,
	timeToCompleteMap,
	weeklyTimeMap,
} from './answerMapping';
import {
	AnswerScreens,
	AnswerScreensToCatalogServiceScreenName,
	Answers,
	EducationValues,
	FilterParams,
} from './types';

const numberRegex = /\d\.?\d*/;

const getMinValue = (answer?: string): null | number => {
	const result = answer.match(numberRegex);
	return result === null ? null : parseFloat(result[0]);
};

const prefixFacetCategory = (facetValues: string[], facet: keyof typeof AlgoliaFacets) =>
	facetValues.map(value => `${AlgoliaFacets[facet]}: ${value}`);

const parseGoal = (answers: Partial<Answers>): string[] => {
	const { goal } = answers;
	const goalsArray: string[] = goalsFacetMap[goal];
	return goalsArray ? prefixFacetCategory(goalsArray, 'Education goal') : [];
};

const parseEducation = (answers: Partial<Answers>): string[] => {
	const { education } = answers;
	const educationArray: string[] = educationFacetMap[education];
	return educationArray ? prefixFacetCategory(educationArray, 'Education goal') : [];
};

const parseEduLevel = (answer?: string): [number, number] | null => {
	return eduLevelMap[answer] || null;
};

const parseTimeToComplete = (answer?: string): number | null => {
	return timeToCompleteMap[answer] || null;
};

const parseWeeklyTime = (answer?: string): number | null => {
	return weeklyTimeMap[answer] || null;
};

const parseHsGpa = (answer?: string): number | null => {
	return gpaMap[answer] || null;
};

const parseInterests = (answers: Partial<Answers>): string[] => {
	const { interests, techInterests, businessInterests, healthcareInterests } = answers;
	// "I'm not sure" is effectively not filtering by interests
	if (!interests || (interests.includes("I'm not sure") && interests.length === 1)) {
		return [];
	}

	// Start with all of the interests answers
	let parsedInterests = [...interests];
	const subInterests: string[] = [];
	// Collect sub-interests making sure they're arrays with wildcard values stripped out
	const parsedTechInterests = (techInterests || []).filter(i => i !== 'All technology programs');
	const parsedBusinessInterests = (businessInterests || []).filter(
		i => i !== 'All business programs'
	);

	const parsedHealthcareInterests = (healthcareInterests || []).filter(
		i => i !== 'All healthcare programs'
	);

	// If we have sub-interests for Technology then remove the parent so we filter by just the children
	if (parsedTechInterests.length) {
		parsedInterests = parsedInterests.filter(i => i !== 'Technology');
		parsedInterests.push(...parsedInterests);
		subInterests.push(...parsedTechInterests);
	}

	// If we have sub-interests for Business then remove the parent so we filter by just the children
	if (parsedBusinessInterests.length) {
		parsedInterests = parsedInterests.filter(i => i !== 'Business');
		parsedInterests.push(...parsedInterests);
		subInterests.push(...parsedBusinessInterests);
	}

	// If we have sub-interests for Healthcare then remove the parent so we filter by just the children
	if (parsedHealthcareInterests.length) {
		parsedInterests = parsedInterests.filter(i => i !== 'Healthcare');
		parsedInterests.push(...parsedInterests);
		subInterests.push(...parsedHealthcareInterests);
	}
	// Prefix with the facet name
	const parentInterests = prefixFacetCategory(parsedInterests, 'Interests');
	const childrenInterests = prefixFacetCategory(subInterests, 'Interests children');
	return [...parentInterests, ...childrenInterests];
};

const getFilterParams = (answers: Partial<Answers>, filter: string[]): FilterParams => {
	// Filters: hs gpa, college gpa, education, credits, timeToComplete, weeklyTime
	// Facets: interests, techInterests, businessInterests, healthcareInterests
	const {
		credits = '',
		hsGpa = '',
		collegeGpa = '',
		timeToComplete,
		weeklyTime,
		...interestAnswers
	} = answers;
	const minHsGpa = parseHsGpa(hsGpa);
	const minCollegeGpa = getMinValue(collegeGpa);
	const parsedCollegeCredits = getMinValue(credits);
	const minCollegeCredits = parsedCollegeCredits < 60 ? parsedCollegeCredits : 120;
	const educationGoalFacets = parseGoal(answers);
	const eduLevelFilter = parseEduLevel(answers.education);
	const timeToCompleteFilter = parseTimeToComplete(timeToComplete);
	const weeklyTimeFilter = parseWeeklyTime(weeklyTime);
	const filterOptions = filter;

	if (answers.education === EducationValues['Some high school']) {
		filterOptions.push(
			`${AlgoliaFilters.minHsGpa} = 0 AND ${AlgoliaFilters.minCollegeCredits} = 0`
		);
	}

	if (answers.education === EducationValues['High school diploma']) {
		filterOptions.push(`${AlgoliaFilters.minCollegeCredits} = 0`);
	}

	if (Number.isFinite(minCollegeGpa)) {
		filterOptions.push(`${AlgoliaFilters.minCollegeGpa} <= ${minCollegeGpa}`);
	}

	if (Number.isFinite(minHsGpa)) {
		filterOptions.push(`${AlgoliaFilters.minHsGpa} <= ${minHsGpa}`);
	}

	if (Number.isFinite(minCollegeCredits)) {
		filterOptions.push(`${AlgoliaFilters.minCollegeCredits} <= ${minCollegeCredits}`);
	}

	if (eduLevelFilter) {
		const learnLanguageGoal =
			educationGoalFacets.length === 1 &&
			educationGoalFacets[0] === `${AlgoliaFacets['Education goal']}: Learn a Language`;
		const lower = learnLanguageGoal ? 0 : eduLevelFilter[0];

		filterOptions.push(`${AlgoliaFilters.minEduLevel} >= ${lower}`);
		filterOptions.push(`${AlgoliaFilters.minEduLevel} <= ${eduLevelFilter[1]}`);
	}

	if (timeToCompleteFilter) {
		filterOptions.push(`${AlgoliaFilters.minCompletionDays} <= ${timeToCompleteFilter}`);
	}

	if (weeklyTimeFilter) {
		filterOptions.push(`${AlgoliaFilters.maxEffortHours} <= ${weeklyTimeFilter}`);
		filterOptions.push(`${AlgoliaFilters.minEffortHours} <= ${weeklyTimeFilter}`);
	}

	const interestsFilter = parseInterests(interestAnswers);
	const educationFilter = parseEducation(answers);

	return {
		facetFilters: [educationFilter, educationGoalFacets, interestsFilter],
		filters: filterOptions.join(' AND '),
	};
};

export const getRemainingFacetsFromAnswers = async (
	answers: Answers = {},
	facetName: string,
	baseFilters: string,
	index: string
): Promise<string[]> => {
	const filter = [baseFilters];
	const { facetFilters, filters } = getFilterParams(answers, filter);

	const facets = await searchForFacetValues(
		facetName,
		'*',
		{
			facetFilters,
			filters,
			maxFacetHits: 100,
		},
		index
	);

	return facets.facetHits.map(facet => facet.value);
};

export const getGoals = async (
	answers: Answers = {},
	baseFilters: string,
	index: string
): Promise<{ label: string; disabled: boolean }[]> => {
	const { education } = answers;
	const facets = await getRemainingFacetsFromAnswers(
		{ education },
		`${AlgoliaFacets['Education goal']}`,
		baseFilters,
		index
	);

	return educationalGoals
		.filter(category => {
			return category.facets.length === 0 || category.facets.some(facet => facets.includes(facet));
		})
		.map(option => {
			return { label: option.name, disabled: false };
		});
};

export const getCurrentRecommendations = async (
	answers: Answers = {},
	userId: string,
	index: string,
	baseFilters: string
): Promise<string[]> => {
	const filter = [baseFilters];
	const { facetFilters, filters } = getFilterParams(answers, filter);
	const params = {
		facetFilters,
		filters,
		hitsPerPage: 1000,
		userToken: userId,
	};

	const response = await search('', params, index);
	return (response.hits as unknown[] as OfferingHit[]).map(result => result.id);
};

const rebuildAnswers = (answer: Partial<Answers>, answers: Answers) => {
	// We rebuild the answers object by looping through existing keys and ignoring anything
	// newer (has a later position in the keys array) than the current answer. This method
	// relies on the order properties are added to the object to be consistent, so keep that
	// in mind when modifying this code.
	const answerKey = Object.keys(answer).pop();
	const answerKeys = Object.keys(answers);
	const answerIndex = answerKeys.indexOf(answerKey);
	const newAnswers = answerKeys.slice(0, answerIndex).reduce((answersObj, key) => {
		answersObj[key] = answers[key];
		return answersObj;
	}, {});
	newAnswers[answerKey] = answer[answerKey];
	return newAnswers;
};

export const updateAnswers = (answer: Partial<Answers>, answers: Answers) => {
	const answerKey = Object.keys(answer).pop();
	let newAnswers: Answers;

	// If a value for the current answer key exists and is different than the incoming
	// answer, then build a new answers object without answers past the current step.
	if (answers[answerKey] && answers[answerKey] !== answer[answerKey]) {
		newAnswers = rebuildAnswers(answer, answers);
	} else {
		newAnswers = { ...answers, ...answer };
	}

	Object.assign(answers, newAnswers);
	return newAnswers;
};

export const formatResponses = (answers: Answers, screenHistory: string[]) => {
	const screens = Object.keys(answers);
	const validResponseScreens = Object.keys(AnswerScreensToCatalogServiceScreenName);

	const responses: CreateQuizResponseInput[] = screens
		.filter(screen => validResponseScreens.includes(screen))
		.map(screen => {
			if (AnswerScreensToCatalogServiceScreenName[screen]) {
				return {
					screenName: AnswerScreensToCatalogServiceScreenName[screen],
					ordering: screenHistory.indexOf(AnswerScreens[screen]),
					source: QuizSource.CATALOG_FRONTEND,
					response: {
						[screenSelectionTypeMap[screen]]: answers[screen],
					},
				};
			}
			return null;
		});
	return responses;
};
